import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight } from 'lucide-react';
import { mockPricingPlans } from '../mock';
import { getSettingsSync } from '../utils/settings';
import './Pricing.css';

const Pricing = () => {
  const [settings, setSettings] = useState(getSettingsSync());

  useEffect(() => {
    const loadSettings = async () => {
      const { getSettings } = await import('../utils/settings');
      const fetchedSettings = await getSettings();
      setSettings(fetchedSettings);
    };
    loadSettings();
    
    const handleSettingsUpdate = () => {
      loadSettings();
    };
    
    window.addEventListener('settingsUpdated', handleSettingsUpdate);
    return () => window.removeEventListener('settingsUpdated', handleSettingsUpdate);
  }, []);

  // Use settings pricing plans or fall back to mock data
  const pricingPlans = settings.pricingPlans || mockPricingPlans;

  return (
    <div className="pricing-page">
      <section className="page-hero">
        <h1 className="display-huge">Simple, Transparent Pricing</h1>
        <p className="body-large" style={{ marginTop: '32px', maxWidth: '800px', margin: '32px auto 0' }}>
          Choose the plan that fits your business size and scale as you grow.
        </p>
      </section>

      <section className="pricing-section">
        <div className="pricing-grid">
          {pricingPlans.map((plan, idx) => (
            <div 
              key={idx} 
              className={`pricing-card ${plan.popular ? 'popular' : ''} dark-hover`}
            >
              {plan.popular && (
                <div className="popular-badge">Most Popular</div>
              )}
              <h3 className="heading-2">{plan.name}</h3>
              <div className="price">
                <span className="price-amount">{plan.price}</span>
                <span className="price-period">{plan.period}</span>
              </div>
              <p className="body-small" style={{ marginTop: '16px', color: 'var(--text-muted)' }}>
                {plan.description}
              </p>
              <ul className="features-list">
                {plan.features && plan.features.map((feature, fIdx) => (
                  <li key={fIdx} className="body-small">
                    <Check size={20} color="var(--brand-primary)" />
                    {feature}
                  </li>
                ))}
              </ul>
              <Link 
                to={plan.ctaLink || '/demo'}
                className={plan.popular ? 'btn-primary' : 'btn-secondary'}
                style={{ width: '100%', marginTop: '32px' }}
              >
                {plan.cta}
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Enterprise Features */}
      <section className="enterprise-section">
        <h2 className="display-large" style={{ textAlign: 'center' }}>Enterprise Features</h2>
        <div className="enterprise-grid">
          <div className="enterprise-feature">
            <h3 className="heading-3">Custom Training</h3>
            <p className="body-small" style={{ marginTop: '12px' }}>Train AI agents on your specific data and workflows for maximum accuracy.</p>
          </div>
          <div className="enterprise-feature">
            <h3 className="heading-3">White Label</h3>
            <p className="body-small" style={{ marginTop: '12px' }}>Complete branding customization to match your company identity.</p>
          </div>
          <div className="enterprise-feature">
            <h3 className="heading-3">SLA Guarantees</h3>
            <p className="body-small" style={{ marginTop: '12px' }}>99.9% uptime guarantee with dedicated support and response times.</p>
          </div>
          <div className="enterprise-feature">
            <h3 className="heading-3">Security Audits</h3>
            <p className="body-small" style={{ marginTop: '12px' }}>Regular security assessments and compliance reporting.</p>
          </div>
          <div className="enterprise-feature">
            <h3 className="heading-3">Dedicated Infrastructure</h3>
            <p className="body-small" style={{ marginTop: '12px' }}>Isolated compute resources for maximum performance and security.</p>
          </div>
          <div className="enterprise-feature">
            <h3 className="heading-3">Priority Support</h3>
            <p className="body-small" style={{ marginTop: '12px' }}>24/7 dedicated support team with direct access to engineers.</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pricing-cta">
        <h2 className="display-large">Not Sure Which Plan?</h2>
        <p className="body-large" style={{ marginTop: '24px' }}>
          Book a demo and we'll help you find the perfect fit for your needs.
        </p>
        <div style={{ marginTop: '40px' }}>
          <Link to="/demo" className="btn-primary">
            Talk to Sales <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Pricing;